import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import ReusableTable from "../../dashboard/ReusableTable";
import ReusableForm from "../../common/ReusableForm";
import ReusableDeleteDialog from '../../common/ReusableDeleteDialog';
import { getRequest, postRequest, putRequest, deleteRequest } from "../../services/apiService";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from 'primereact/dropdown';
import EquipmentActivityMapping from "./EquipmentActivityMapping";

const EquipmentManager = ({ onBack }) => {
  const toast = useRef(null);
  const [equipment, setEquipment] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActivityMappingModal, setShowActivityMappingModal] = useState(false);
  const [currentEquipment, setCurrentEquipment] = useState(null);
  const [equipmentToDelete, setEquipmentToDelete] = useState(null);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [allActivities, setAllActivities] = useState([]);
  const [dropdownData, setDropdownData] = useState({
    compartments: [],
    categories: [],
    departments: [],
    systems: [],
    activities: []
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [showActivitySteps, setShowActivitySteps] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    category: "",
    department: "",
    activity: "",
    status_db: 1
  });

  // Delete confirmation form fields
  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirmation",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  // Delete form state
  const [deleteFormData, setDeleteFormData] = useState({
    confirmation: ""
  });
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  // Fetch data from APIs
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [
          compartmentsRes,
          categoriesRes,
          departmentsRes,
          systemsRes,
          activitiesRes,
          equipmentRes
        ] = await Promise.all([
          getRequest('master/compartments/'),
          getRequest('master/categories/'),
          getRequest('master/departments/'),
          getRequest('master/systems/'),
          getRequest('master/activities/'),
          getRequest(`master/equipment/?page=${currentPage}`)
        ]);

        setDropdownData({
          compartments: compartmentsRes?.data || [],
          categories: categoriesRes?.data || [],
          departments: departmentsRes?.data || [],
          systems: systemsRes?.data || [],
          activities: activitiesRes?.data || []
        });

        setEquipment(equipmentRes?.data || equipmentRes?.results?.data || []);
        setTotalRecords(equipmentRes?.count || equipmentRes?.results?.count || 0);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch data",
          life: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage]);

  // Fetch activities for mapping
  const fetchActivitiesForMapping = async (equipmentId) => {
    setMappingLoading(true);
    try {
      // Fetch all available activities
      const allActivitiesRes = await getRequest('master/equipment-activity/?dropdown=true');
      setAllActivities(allActivitiesRes || []);

      // Fetch mapped activities for this equipment
      const mappedRes = await getRequest(`master/equipment-activity-mapping?equipment_id=${equipmentId}`);
      const mappedActivityIds = mappedRes.results?.data?.map(item => item.equipment_activity.id) || [];
      setSelectedActivities(mappedActivityIds);
    } catch (error) {
      console.error("Error fetching activities:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch activities",
        life: 3000,
      });
    } finally {
      setMappingLoading(false);
    }
  };

  const openActivityMappingModal = (equipment) => {
    setCurrentEquipment(equipment);
    setShowActivityMappingModal(true);
  };

  const handleActivityToggle = (activityId) => {
    setSelectedActivities(prev => 
      prev.includes(activityId) 
        ? prev.filter(id => id !== activityId) 
        : [...prev, activityId]
    );
  };

  const saveActivityMappings = async () => {
    try {
      setMappingLoading(true);
  
      // 1. Get existing mappings for this equipment
      const existingMappings = await getRequest(
        `master/equipment-activity-mapping/?equipment_id=${currentEquipment.id}&dropdown=true`
      ) || [];
  
      // 2. Prepare payload combining existing and new activities
      const payload = selectedActivities.map(activityId => {
        // Find existing mapping if it exists
        const existingMapping = existingMappings.find(
          m => m.equipment_activity.id === activityId
        );
        
        return {
          id: existingMapping ? existingMapping.id : null,
          equipment_activity: activityId
        };
      });
  
      // 3. If no activities selected, send empty array to clear all
      const finalPayload = selectedActivities.length > 0 ? payload : [];
  
      // 4. Always use POST with equipment ID in URL
      await postRequest(
        `master/equipment-activity-mapping/${currentEquipment.id}/`,
        finalPayload
      );
  
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: selectedActivities.length > 0 
          ? "Activity mappings saved successfully!"
          : "All activity mappings cleared",
        life: 3000,
      });
  
      setShowActivityMappingModal(false);
    } catch (error) {
      console.error("Error saving activity mappings:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save activity mappings",
        life: 3000,
      });
    } finally {
      setMappingLoading(false);
    }
  };
  // Form fields configuration
  const equipmentFormFields = [
    {
      name: "name",
      label: "Equipment Name",
      type: "text",
      required: true,
      placeholder: "Enter equipment name",
      helpText: "Enter a unique name for the equipment"
    },
    {
      name: "category",
      label: "Category",
      type: "select",
      required: true,
      options: [
        { label: "Select Category", value: "" },
        ...(dropdownData.categories || []).map(cat => ({
          label: cat.name,
          value: cat.id
        }))
      ],
      helpText: "Select the category for this equipment"
    },
    {
      name: "department",
      label: "Department",
      type: "select",
      required: true,
      options: [
        { label: "Select Department", value: "" },
        ...(dropdownData.departments || []).map(dept => ({
          label: dept.name,
          value: dept.id
        }))
      ],
      helpText: "Select the department responsible for this equipment"
    },
    {
      name: "activity",
      label: "Activity",
      type: "select",
      required: true,
      options: [
        { label: "Select Activity", value: "" },
        ...(dropdownData.activities || []).map(act => ({
          label: act.name,
          value: act.id
        }))
      ],
      helpText: "Select the activity this equipment is used for"
    },
  ];

  const handleSave = async () => {
    if (!formData.name || !formData.category || !formData.department || !formData.activity) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please fill in all required fields",
        life: 3000,
      });
      return;
    }
  
    const payload = {
      ...formData,
      category: typeof formData.category === "object" ? formData.category.id : formData.category,
      department: typeof formData.department === "object" ? formData.department.id : formData.department,
      activity: typeof formData.activity === "object" ? formData.activity.id : formData.activity,
    };
  
    try {
      if (formData.id) {
        // Use POST for update with specific endpoint
        await postRequest("master/equipment/", payload);
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Equipment updated successfully!",
          life: 3000,
        });
      } else {
        await postRequest("master/equipment/", payload);
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Equipment added successfully!",
          life: 3000,
        });
        setCurrentPage(1);
      }
  
      const response = await getRequest(`master/equipment/?page=${currentPage}`);
      setEquipment(response?.data || response?.results?.data || []);
      setTotalRecords(response?.count || response?.results?.count || 0);
  
      setShowAddModal(false);
      setShowEditModal(false);
      setFormData({
        id: null,
        name: "",
        category: "",
        department: "",
        activity: "",
        status_db: 1
      });
    } catch (error) {
      console.error("Error saving equipment:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save equipment",
        life: 3000,
      });
    }
  };

  const openAddModal = () => {
    setFormData({
      id: null,
      name: "",
      category: "",
      department: "",
      activity: "",
      status_db: 1
    });
    setShowAddModal(true);
  };

  const openEditModal = (equipment) => {
    setFormData({
      id: equipment.id,
      name: equipment.name,
      category: equipment.category?.id || "",
      department: equipment.department?.id || "",
      activity: equipment.activity?.id || "",
      status_db: equipment.status_db ?? 1,
    });
    setShowEditModal(true);
  };

  const handleDelete = async () => {
    if (deleteFormData.confirmation !== "DELETE") {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please type 'DELETE' to confirm",
        life: 3000,
      });
      return;
    }

    try {
      // Use POST for soft delete with status_db: 3
      await postRequest("master/equipment/", {
        id: equipmentToDelete.id,
        status_db: 3
      });
      
      if (equipment.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        const response = await getRequest(`master/equipment/?page=${currentPage}`);
        setEquipment(response?.data || response?.results?.data || []);
        setTotalRecords(response?.count || response?.results?.count || 0);
      }
      
      setShowDeleteModal(false);
      setEquipmentToDelete(null);
      setDeleteFormData({ confirmation: "" });
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Equipment deleted successfully!",
        life: 3000,
      });
    } catch (error) {
      console.error("Error deleting equipment:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete equipment",
        life: 3000,
      });
    }
  };

  const onPageChange = (event) => {
    setCurrentPage(event.page + 1);
    setRowsPerPage(event.rows);
  };

  const openDeleteModal = (equipment) => {
    setEquipmentToDelete(equipment);
    setDeleteFormData({ confirmation: "" });
    setShowDeleteModal(true);
  };

  const columnFields = [
    { field: "name", header: "Equipment Name" },
    { field: "code", header: "Code" },
    { 
      field: "category.name", 
      header: "Category",
      body: (rowData) => rowData.category?.name || '-' 
    },
    { 
      field: "department.name", 
      header: "Department",
      body: (rowData) => rowData.department?.name || '-' 
    },
    // { 
    //   field: "status_db", 
    //   header: "Status",
    //   body: (rowData) => (
    //     <span className={`px-2 py-1 rounded-full text-xs ${
    //       rowData.status_db === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
    //     }`}>
    //       {rowData.status_db === 1 ? 'Active' : 'Inactive'}
    //     </span>
    //   )
    // }
  ];

  const actionItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: (rowData) => openEditModal(rowData),
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: (rowData) => openDeleteModal(rowData),
    },
    {
      label: "Activity Mapping",
      icon: "pi pi-share-alt",
      command: (rowData) => openActivityMappingModal(rowData),
    },
  ];
  // Fetch static dropdown data once
useEffect(() => {
  const fetchDropdownData = async () => {
    try {
      const [compartmentsRes, categoriesRes, departmentsRes, systemsRes, activitiesRes] = 
        await Promise.all([
          getRequest('master/compartments/'),
          getRequest('master/categories/'),
          getRequest('master/departments/'),
          getRequest('master/systems/'),
          getRequest('master/activities/')
        ]);

      setDropdownData({
        compartments: compartmentsRes?.data || [],
        categories: categoriesRes?.data || [],
        departments: departmentsRes?.data || [],
        systems: systemsRes?.data || [],
        activities: activitiesRes?.data || []
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load dropdown data",
        life: 3000,
      });
    }
  };

  fetchDropdownData();
}, []);
// Debounced search effect for table search
useEffect(() => {
  if (searchTimeout.current) {
    clearTimeout(searchTimeout.current);
  }
  searchTimeout.current = setTimeout(() => {
    setLoading(true);
    const url = `master/equipment/?page=${currentPage}${selectedDepartment ? `&department=${selectedDepartment}` : ''}${search ? `&search=${encodeURIComponent(search)}` : ''}`;
    getRequest(url)
      .then(response => {
        setEquipment(response?.data || response?.results?.data || []);
        setTotalRecords(response?.count || response?.results?.count || 0);
      })
      .catch(() => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch equipment",
          life: 3000,
        });
      })
      .finally(() => setLoading(false));
  }, 700); // 700ms debounce
  return () => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
  };
}, [currentPage, selectedDepartment, search]);

const handleTableSearch = (value) => {
  setSearch(value);
};

  return (
    <div className="min-h-screen bg-gray-100 py-4 px-4">
      <Toast ref={toast} />
      <div className="max-w-full mx-auto">
        <div className="flex justify-between items-center mb-4">
          {/* <h2 className="text-2xl font-bold text-gray-800">Equipment Management</h2> */}
          {/* <div className="flex gap-4">
            <Button
              label="Back"
              icon="pi pi-arrow-left"
              className="p-button-secondary"
              onClick={onBack}
            />
          </div> */}
        </div>
      
        {/* Table Section */}
        <div className="mb-4 p-fluid flex justify-end">
          <Dropdown
            value={selectedDepartment}
            options={[
              { label: "", value: null },
              ...dropdownData.departments.map(dept => ({
                label: dept.name,
                value: dept.id
              }))
            ]}
            onChange={(e) => {
              setSelectedDepartment(e.value);
              setCurrentPage(1);
            }}
            placeholder="Filter by Department"
            showClear
            className="w-64 md:w-14rem text-center border-1 border-black"
          />
        </div>
        <div className="mt-0">
          <ReusableTable
            title="Equipment Management"
            data={equipment}
            setData={setEquipment}
            columnFields={columnFields}
            setAddNewDialogVisible={openAddModal}
            actionMenu={true}
            actionItems={actionItems}
            loading={loading}
            totalRecords={totalRecords}
            rows={rowsPerPage}
            onPageChange={onPageChange}
            first={(currentPage - 1) * rowsPerPage}
            emptyMessage="No equipment records found. Add new equipment to get started."
            searchValue={search}
            onSearchChange={handleTableSearch}
          />
        </div>

        {/* Add Equipment Dialog */}
        {(showAddModal || showEditModal) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className={`bg-white rounded-xl shadow-2xl w-full mx-4 animate-fade-in ${equipmentFormFields.length > 6 ? 'max-w-4xl' : 'max-w-2xl'}`}>
              <ReusableForm
                formData={formData}
                setFormData={setFormData}
                fields={equipmentFormFields}
                onSubmit={handleSave}
                onHide={() => (showEditModal ? setShowEditModal(false) : setShowAddModal(false))}
                loading={loading}
                headerText={showEditModal ? "Edit Equipment" : "Add Equipment"}
              />
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <ReusableDeleteDialog
          visible={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          onDelete={handleDelete}
          fields={deleteFormFields}
          formData={deleteFormData}
          setFormData={setDeleteFormData}
          loading={loading}
          title="Delete Equipment"
          description={`Are you sure you want to delete equipment "${equipmentToDelete?.name}"? This action cannot be undone. Type 'DELETE' to confirm.`}
          deleteLabel="Delete"
        />

      
        {showActivityMappingModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col animate-fade-in">
              <div className="p-4 border-b flex justify-between items-center bg-gray-100 rounded-t-xl">
                <h3 className="text-lg font-semibold">{`Activity Sequence for ${currentEquipment?.name || "Equipment"}`}</h3>
                <Button 
                  icon="pi pi-times" 
                  className="p-button-rounded p-button-text" 
                  onClick={() => setShowActivityMappingModal(false)} 
                />
              </div>
              <div className="overflow-y-auto flex-grow">
                <EquipmentActivityMapping equipmentId={currentEquipment?.id} />
              </div>
            </div>
          </div>
        )}

        {showActivitySteps && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <EquipmentActivityMapping 
              onBack={() => {
                setShowActivitySteps(false);
                setShowActivityMappingModal(true);
              }}
              onClose={() => setShowActivitySteps(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EquipmentManager;